import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Bouton de la plateforme.
 * - `primary` : couleur de marque du garage (une seule action principale par écran) ;
 * - `secondary` : surface blanche bordée ; `quiet` : discret, sans bordure ; `danger` : action destructrice.
 * Tailles (passe « densité », hauteurs dans `styles.css`) : au doigt, cibles de 44 px (`lg` 48 px) ;
 * au pointeur fin (souris), contrôles compacts : `sm` 30 px, `compact` et `md` 34 px, `lg` 36 px.
 * `compact` : deux boutons côte à côte sur téléphone (marges serrées).
 * `loading` désactive le bouton et annonce l'état (`aria-busy`).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';
export type ButtonSize = 'sm' | 'compact' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-on-brand ring-1 ring-inset ring-black/10 hover:brightness-[1.06] active:brightness-95',
  secondary:
    'bg-surface text-ink ring-1 ring-inset ring-line-strong hover:bg-hover hover:ring-ink-3/60 active:bg-sunken',
  quiet: 'bg-transparent text-ink-2 hover:bg-sunken hover:text-ink active:bg-line/60',
  danger:
    'bg-surface text-danger-fg ring-1 ring-inset ring-danger-line hover:bg-danger-bg active:bg-danger-bg',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-(--ctl-sm) px-3 text-sm gap-1.5',
  compact: 'min-h-(--ctl-md) px-2.5 text-sm gap-1.5 sm:px-3.5',
  md: 'min-h-(--ctl-md) px-3.5 text-sm gap-1.5',
  lg: 'min-h-(--ctl-lg) px-4 text-base gap-2 pointer-fine:text-sm',
};

/** Classes d'un bouton, pour un lien (`<Link>`, `<a>`) qui doit en avoir l'apparence. */
export function buttonClasses({
  variant = 'primary',
  size = 'md',
  block = false,
  className,
}: { variant?: ButtonVariant; size?: ButtonSize; block?: boolean; className?: string } = {}): string {
  return cx(
    'inline-flex select-none items-center justify-center rounded-xl py-1.5 font-medium leading-tight no-underline',
    'transition-[background-color,box-shadow,filter,color] duration-150 ease-out',
    'disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:cursor-not-allowed aria-disabled:opacity-45',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    VARIANT[variant],
    SIZE[size],
    block && 'w-full',
    className,
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx(
        'inline-block size-[1.1em] shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  /** Icône placée avant le libellé (remplacée par l'indicateur pendant le chargement). */
  icon?: ReactNode;
  children: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    loading = false,
    icon,
    className,
    children,
    type = 'button',
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses({ variant, size, block, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
});

/** Bouton carré pour une icône seule : `label` est obligatoire (nom accessible). */
export function IconButton({
  label,
  children,
  variant = 'quiet',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  variant?: ButtonVariant;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={buttonClasses({ variant, size: 'sm', className: cx('min-w-(--ctl-sm) px-0', className) })}
      {...rest}
    >
      {children}
    </button>
  );
}
